import { useState } from "react";
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Package,
  Truck,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { api } from "../utils/api";
import toast from "react-hot-toast";
import { Button } from "./ui/Button";

const STEPS = {
  VERIFY_DATA: 1,
  PRINT_LABEL: 2,
  CONFIRM_SHIPPED: 3,
};

export function ShippingModal({ booking, onClose, onSuccess }) {
  const [currentStep, setCurrentStep] = useState(STEPS.VERIFY_DATA);
  const [isGeneratingLabel, setIsGeneratingLabel] = useState(false);
  const [isMarkingShipped, setIsMarkingShipped] = useState(false);
  const [labelUrl, setLabelUrl] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState(
    booking.trackingNumber || null,
  );
  const [isEditingAddress, setIsEditingAddress] = useState(false);

  // Checklist state for step 3
  const [checklist, setChecklist] = useState({
    complete: false,
    packed: false,
    labeled: false,
    ready: false,
    returnLabel: false,
  });

  // Customer and shipping data (editable)
  const [customerData, setCustomerData] = useState({
    name: booking.customerName || booking.shippingAddress?.name || "",
    email: booking.customerEmail || "",
    phone: booking.shippingAddress?.phone || booking.customerPhone || "",
    street: booking.shippingAddress?.street || "",
    street_number: booking.shippingAddress?.street_number || "",
    postal_code: booking.shippingAddress?.postal_code || "",
    city: booking.shippingAddress?.city || "",
    country: booking.shippingAddress?.country || "Deutschland",
  });

  // Helper function to download and optionally print PDF
  const downloadAndPrintLabel = (labelUrl, bookingId) => {
    // Normalize URL - remove leading /uploads/ if present
    const normalizedUrl = labelUrl.startsWith("uploads/")
      ? `/${labelUrl}`
      : labelUrl.startsWith("/uploads/")
        ? labelUrl
        : `/uploads/${labelUrl}`;

    // Open in new tab for printing (user can also save from there)
    const printWindow = window.open(normalizedUrl, "_blank");
    if (printWindow) {
      printWindow.addEventListener("load", () => {
        // Auto-print after 1 second
        setTimeout(() => {
          printWindow.print();
        }, 1000);
      });
    }
  };

  const handleGenerateLabel = async () => {
    setIsGeneratingLabel(true);
    const loadingToast = toast.loading("Erstelle Versandlabel...");

    try {
      const response = await api.createShippingLabel(booking.bookingId);

      if (response.success) {
        setLabelUrl(response.labelUrl);
        setTrackingNumber(response.trackingNumber);
        toast.success("Versandlabel erstellt!", { id: loadingToast });

        // Auto-download and print label
        downloadAndPrintLabel(response.labelUrl, booking.bookingId);

        // Auto-advance to next step
        setTimeout(() => {
          setCurrentStep(STEPS.CONFIRM_SHIPPED);
        }, 500);
      } else {
        toast.error(response.error || "Fehler beim Erstellen des Labels", {
          id: loadingToast,
        });
      }
    } catch (error) {
      console.error("Error creating shipping label:", error);
      toast.error(error.message || "Fehler beim Erstellen des Labels", {
        id: loadingToast,
      });
    } finally {
      setIsGeneratingLabel(false);
    }
  };

  const handleMarkAsShipped = async () => {
    if (!trackingNumber) {
      toast.error("Bitte erst Versandlabel erstellen!");
      return;
    }

    setIsMarkingShipped(true);
    const loadingToast = toast.loading("Markiere als versendet...");

    try {
      const response = await api.markAsShipped(booking.bookingId);

      if (response.success) {
        toast.success(
          "Bestellung als versendet markiert! Kunde erhält Email.",
          { id: loadingToast },
        );

        if (onSuccess) {
          onSuccess();
        }

        // Close modal after success
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        toast.error(response.error || "Fehler beim Aktualisieren", {
          id: loadingToast,
        });
      }
    } catch (error) {
      console.error("Error marking as shipped:", error);
      toast.error(error.message || "Fehler beim Aktualisieren", {
        id: loadingToast,
      });
    } finally {
      setIsMarkingShipped(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center space-x-4">
        {/* Step 1 */}
        <div className="flex items-center">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
              currentStep >= STEPS.VERIFY_DATA
                ? "bg-primary text-white"
                : "bg-gray-200 text-gray-500"
            }`}
          >
            1
          </div>
          <span className="ml-2 text-sm font-medium text-gray-700">
            Daten prüfen
          </span>
        </div>

        {/* Divider */}
        <div
          className={`w-12 h-1 ${
            currentStep >= STEPS.PRINT_LABEL ? "bg-primary" : "bg-gray-200"
          }`}
        />

        {/* Step 2 */}
        <div className="flex items-center">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
              currentStep >= STEPS.PRINT_LABEL
                ? "bg-primary text-white"
                : "bg-gray-200 text-gray-500"
            }`}
          >
            2
          </div>
          <span className="ml-2 text-sm font-medium text-gray-700">
            Label drucken
          </span>
        </div>

        {/* Divider */}
        <div
          className={`w-12 h-1 ${
            currentStep >= STEPS.CONFIRM_SHIPPED ? "bg-primary" : "bg-gray-200"
          }`}
        />

        {/* Step 3 */}
        <div className="flex items-center">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
              currentStep >= STEPS.CONFIRM_SHIPPED
                ? "bg-primary text-white"
                : "bg-gray-200 text-gray-500"
            }`}
          >
            3
          </div>
          <span className="ml-2 text-sm font-medium text-gray-700">
            Versenden
          </span>
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            Versanddaten überprüfen
          </h3>
          <p className="text-sm text-gray-600">
            Bitte überprüfe die Versanddaten vor dem Drucken des Labels.
          </p>
        </div>
        {!isEditingAddress && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsEditingAddress(true)}
          >
            Bearbeiten
          </Button>
        )}
      </div>

      {/* Customer Info */}
      <div className="p-4 space-y-3 rounded-lg bg-gray-50">
        <h4 className="mb-3 font-medium text-gray-900">Kundeninformationen</h4>

        <div className="flex items-center gap-3">
          <User className="w-5 h-5 text-gray-400" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              {customerData.name || "Name nicht verfügbar"}
            </p>
            <p className="text-xs text-gray-500">Kunde</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-gray-400" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              {customerData.email}
            </p>
            <p className="text-xs text-gray-500">Email</p>
          </div>
        </div>

        {customerData.phone && (
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">
                {customerData.phone}
              </p>
              <p className="text-xs text-gray-500">Telefon</p>
            </div>
          </div>
        )}
      </div>

      {/* Shipping Address */}
      <div className="p-4 space-y-3 rounded-lg bg-gray-50">
        <h4 className="mb-3 font-medium text-gray-900">Versandadresse</h4>

        {isEditingAddress ? (
          <div className="space-y-3">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Straße
              </label>
              <input
                type="text"
                value={customerData.street}
                onChange={(e) =>
                  setCustomerData({ ...customerData, street: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Straße"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Hausnummer (optional)
              </label>
              <input
                type="text"
                value={customerData.street_number}
                onChange={(e) =>
                  setCustomerData({
                    ...customerData,
                    street_number: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Hausnummer"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  PLZ
                </label>
                <input
                  type="text"
                  value={customerData.postal_code}
                  onChange={(e) =>
                    setCustomerData({
                      ...customerData,
                      postal_code: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="PLZ"
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Stadt
                </label>
                <input
                  type="text"
                  value={customerData.city}
                  onChange={(e) =>
                    setCustomerData({ ...customerData, city: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Stadt"
                />
              </div>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Land
              </label>
              <input
                type="text"
                value={customerData.country}
                onChange={(e) =>
                  setCustomerData({ ...customerData, country: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Land"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Telefon (optional)
              </label>
              <input
                type="tel"
                value={customerData.phone}
                onChange={(e) =>
                  setCustomerData({ ...customerData, phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Telefon"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  // Reset to original values
                  setCustomerData({
                    name:
                      booking.customerName ||
                      booking.shippingAddress?.name ||
                      "",
                    email: booking.customerEmail || "",
                    phone:
                      booking.shippingAddress?.phone ||
                      booking.customerPhone ||
                      "",
                    street: booking.shippingAddress?.street || "",
                    street_number: booking.shippingAddress?.street_number || "",
                    postal_code: booking.shippingAddress?.postal_code || "",
                    city: booking.shippingAddress?.city || "",
                    country: booking.shippingAddress?.country || "Deutschland",
                  });
                  setIsEditingAddress(false);
                }}
              >
                Abbrechen
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setIsEditingAddress(false);
                  toast.success("Adresse aktualisiert");
                }}
              >
                Speichern
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              {customerData.street ? (
                <>
                  <p className="text-sm font-medium text-gray-900">
                    {customerData.street} {customerData.street_number}
                  </p>
                  <p className="text-sm text-gray-700">
                    {customerData.postal_code} {customerData.city}
                  </p>
                  {customerData.country && (
                    <p className="text-sm text-gray-600">
                      {customerData.country}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500">Adresse nicht verfügbar</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Booking Details */}
      <div className="p-4 space-y-2 rounded-lg bg-primary/10">
        <h4 className="mb-3 font-medium text-gray-900">Buchungsdetails</h4>

        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-primary" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              {booking.productTitle}
            </p>
            <p className="text-xs text-gray-500">Produkt</p>
          </div>
        </div>

        {booking.variantTitle && (
          <div className="flex items-center justify-between px-3 py-2 text-sm bg-white rounded">
            <span className="text-gray-600">Farbvariante:</span>
            <span className="font-semibold text-gray-900">
              {booking.variantTitle}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Event-Datum:</span>
          <span className="font-medium text-gray-900">
            {new Date(booking.startDate).toLocaleDateString("de-DE")}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Buchungs-ID:</span>
          <span className="font-mono text-gray-900">{booking.bookingId}</span>
        </div>
      </div>

      {/* Warning if address incomplete */}
      {(!customerData.street ||
        !customerData.postal_code ||
        !customerData.city) && (
        <div className="flex items-start gap-3 p-4 border border-yellow-200 rounded-lg bg-yellow-50">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-900">
              Warnung: Adresse fehlt
            </p>
            <p className="mt-1 text-sm text-yellow-700">
              Die Versandadresse ist nicht vollständig. Diese Bestellung wurde
              möglicherweise ohne Versandadresse erstellt. Bitte kontaktiere den
              Kunden für die vollständige Adresse.
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="secondary" onClick={onClose}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          onClick={() => setCurrentStep(STEPS.PRINT_LABEL)}
          disabled={
            !customerData.street ||
            !customerData.postal_code ||
            !customerData.city
          }
        >
          Weiter zu Label drucken
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          Versandlabel erstellen
        </h3>
        <p className="mb-6 text-sm text-gray-600">
          Erstelle das DHL-Versandlabel für diese Bestellung.
        </p>
      </div>

      {labelUrl ? (
        <div className="p-6 space-y-4 text-center border border-green-200 rounded-lg bg-green-50">
          <CheckCircle className="w-16 h-16 mx-auto text-green-600" />
          <div>
            <p className="text-lg font-semibold text-green-900">
              Label erstellt!
            </p>
            <p className="mt-2 text-sm text-green-700">
              Sendungsnummer: {trackingNumber}
            </p>
          </div>
          <div className="flex justify-center gap-3">
            <Button
              variant="secondary"
              onClick={() => downloadAndPrintLabel(labelUrl, booking.bookingId)}
            >
              <Package className="w-4 h-4 mr-2" />
              Erneut drucken
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                const normalizedUrl = labelUrl.startsWith("uploads/")
                  ? `/${labelUrl}`
                  : labelUrl.startsWith("/uploads/")
                    ? labelUrl
                    : `/uploads/${labelUrl}`;
                window.open(normalizedUrl, "_blank");
              }}
            >
              Label anzeigen
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-8 space-y-4 text-center border border-gray-200 rounded-lg bg-gray-50">
          <Package className="w-16 h-16 mx-auto text-gray-400" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              Bereit zum Drucken
            </p>
            <p className="mt-1 text-sm text-gray-600">
              Klicke auf "Label erstellen" um das DHL-Versandlabel zu
              generieren.
            </p>
          </div>
          <Button
            variant="primary"
            onClick={handleGenerateLabel}
            disabled={isGeneratingLabel}
            size="lg"
          >
            <Package className="w-5 h-5 mr-2" />
            {isGeneratingLabel
              ? "Erstelle Label..."
              : "Label erstellen & drucken"}
          </Button>
        </div>
      )}

      {/* Instructions */}
      <div className="p-4 rounded-lg bg-primary/10">
        <h4 className="mb-2 font-medium text-gray-900">Nächste Schritte:</h4>
        <ol className="space-y-1 text-sm text-gray-800 list-decimal list-inside">
          <li>Drucke das Versandlabel aus</li>
          <li>Verpacke die Fotobox sorgfältig</li>
          <li>Klebe das Label auf das Paket</li>
          <li>Stelle das Paket zur Abholung bereit</li>
        </ol>
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t">
        <Button
          variant="secondary"
          onClick={() => setCurrentStep(STEPS.VERIFY_DATA)}
        >
          Zurück
        </Button>
        <Button
          variant="primary"
          onClick={() => setCurrentStep(STEPS.CONFIRM_SHIPPED)}
          disabled={!labelUrl}
        >
          Weiter zum Versand
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          Als versendet markieren
        </h3>
        <p className="mb-6 text-sm text-gray-600">
          Bestätige, dass die Bestellung verpackt, gelabelt und zum Versand
          bereitgelegt wurde.
        </p>
      </div>

      {/* Checklist */}
      <div className="p-4 space-y-3 rounded-lg bg-gray-50">
        <h4 className="mb-3 font-medium text-gray-900">Versand-Checkliste</h4>

        <div className="space-y-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={checklist.complete}
              onChange={(e) =>
                setChecklist({ ...checklist, complete: e.target.checked })
              }
            />
            <span className="text-sm text-gray-700">
              Fotobox ist vollständig (alle Komponenten vorhanden)
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={checklist.packed}
              onChange={(e) =>
                setChecklist({ ...checklist, packed: e.target.checked })
              }
            />
            <span className="text-sm text-gray-700">
              Fotobox wurde sorgfältig verpackt
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={checklist.labeled}
              onChange={(e) =>
                setChecklist({ ...checklist, labeled: e.target.checked })
              }
            />
            <span className="text-sm text-gray-700">
              Versandlabel wurde aufgeklebt
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={checklist.ready}
              onChange={(e) =>
                setChecklist({ ...checklist, ready: e.target.checked })
              }
            />
            <span className="text-sm text-gray-700">
              Paket wurde zur Abholung/Übergabe bereitgelegt
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={checklist.returnLabel}
              onChange={(e) =>
                setChecklist({ ...checklist, returnLabel: e.target.checked })
              }
            />
            <span className="text-sm text-gray-700">
              Rücksende-Label wurde beigelegt
            </span>
          </label>
        </div>
      </div>

      {/* Tracking Info */}
      {trackingNumber && (
        <div className="p-4 rounded-lg bg-primary/10">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-5 h-5 text-primary" />
            <h4 className="font-medium text-gray-900">Sendungsnummer</h4>
          </div>
          <p className="font-mono text-lg text-gray-800">{trackingNumber}</p>
          <p className="mt-2 text-xs text-primary">
            Der Kunde erhält automatisch eine Email mit dieser Tracking-Nummer
          </p>
        </div>
      )}

      {/* Warning */}
      <div className="flex items-start gap-3 p-4 border border-yellow-200 rounded-lg bg-yellow-50">
        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-yellow-900">Wichtig</p>
          <p className="mt-1 text-sm text-yellow-700">
            Nach dem Markieren als "versendet" erhält der Kunde automatisch eine
            Email mit der Sendungsverfolgung und den Aufbauinstruktionen.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t">
        <Button
          variant="secondary"
          onClick={() => setCurrentStep(STEPS.PRINT_LABEL)}
        >
          Zurück
        </Button>
        <Button
          variant="success"
          onClick={handleMarkAsShipped}
          disabled={
            isMarkingShipped ||
            !trackingNumber ||
            !checklist.complete ||
            !checklist.packed ||
            !checklist.labeled ||
            !checklist.ready ||
            !checklist.returnLabel
          }
          size="lg"
        >
          <CheckCircle className="w-5 h-5 mr-2" />
          {isMarkingShipped ? "Markiere..." : "Als versendet markieren"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Versand vorbereiten
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Buchung: {booking.bookingId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 transition-colors hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {renderStepIndicator()}

          {currentStep === STEPS.VERIFY_DATA && renderStep1()}
          {currentStep === STEPS.PRINT_LABEL && renderStep2()}
          {currentStep === STEPS.CONFIRM_SHIPPED && renderStep3()}
        </div>
      </div>
    </div>
  );
}

export default ShippingModal;
